/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { firebaseAuth } from "../../firebaseConfig";
import { useUserSession } from "../../context/UserContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useSignup } from "../../hooks";
import { HashLoader } from "react-spinners";
import TextLoader from "../auth/TextLoader";
import { IWorkspace, IWorkspaceAccess, WorkspaceRole } from "../../types";
import i18n from "../../i18n";
import User from "../../models/User";
import {
  normalizeAppLanguage,
  resolveUserPreferences,
  userPreferencesEqual,
} from "../../helpers/userPreferences";

// Helper function to convert Workspace to WorkspaceAccess
const convertWorkspaceToWorkspaceAccess = (
  workspace: IWorkspace,
  userEmail: string,
): IWorkspaceAccess => {
  return {
    workspaceId: workspace.id,
    workspaceName: workspace.name,
    name: userEmail,
    email: userEmail,
    role: WorkspaceRole.Owner,
    isApiKey: false,
    createdBy: workspace.createdBy,
    createdAt: workspace.createdAt,
  };
};

const isSafeContinuePath = (path: string | null): path is string =>
  Boolean(path && path.startsWith("/") && !path.startsWith("//"));

export const WorkspaceLoader: FC = function () {
  const { userSession, setUserSession, persistUserSession, revokeUserSession } =
    useUserSession();
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const location = useLocation();
  const query = useSignup(
    userSession?.token.accessToken ?? "",
    userSession?.user.id ?? "",
  );
  const migratedPreferencesKey = useRef<string>();

  // Check if user came from signup
  const searchParams = new URLSearchParams(location.search);
  const isFromSignup = searchParams.get("from") === "signup";
  const continuePath = searchParams.get("continue");

  useEffect(() => {
    if (userSession && query.isSuccess && query.data) {
      const profileUser = query.data.user;
      const preferences = resolveUserPreferences(
        profileUser?.preferences,
        undefined,
        userSession.user.email,
      );
      const activeLanguage = normalizeAppLanguage(
        i18n.resolvedLanguage ?? i18n.language,
      );
      if (preferences.language && preferences.language !== activeLanguage) {
        void i18n.changeLanguage(preferences.language);
      }

      const nextSession = {
        ...userSession,
        user: {
          ...userSession.user,
          isAdmin: profileUser?.isAdmin ?? false,
          preferences,
        },
      };
      const sessionNeedsUpdate =
        userSession.user.isAdmin !== nextSession.user.isAdmin ||
        !userPreferencesEqual(userSession.user.preferences, preferences);
      if (sessionNeedsUpdate) {
        setUserSession(nextSession);
        persistUserSession(nextSession);
      }

      // Migrate the existing browser preference once for profiles created
      // before user-level preferences were introduced.
      if (
        profileUser?.email &&
        !userPreferencesEqual(profileUser.preferences, preferences)
      ) {
        const migrationKey = `${profileUser.email}:${preferences.language ?? ""}:${preferences.theme}`;
        if (migratedPreferencesKey.current !== migrationKey) {
          migratedPreferencesKey.current = migrationKey;
          void User.updatePreferences(profileUser.email, preferences).catch(
            (error) =>
              console.error("Failed to migrate user preferences:", error),
          );
        }
      }

      const workspaceAccesses = (
        query.data.workspaces as Array<IWorkspace | IWorkspaceAccess>
      ).map((workspace: IWorkspace | IWorkspaceAccess) =>
        "workspaceId" in workspace
          ? workspace
          : convertWorkspaceToWorkspaceAccess(
              workspace,
              userSession.user.email || "",
            ),
      );
      const selectedWorkspace =
        workspaceAccesses.find(
          (workspace) => workspace.workspaceId === activeWorkspace?.workspaceId,
        ) ?? workspaceAccesses[0];

      if (selectedWorkspace) {
        // Refresh the selected workspace from the server instead of replacing it
        // with the first workspace after an OAuth login redirect.
        setActiveWorkspace(selectedWorkspace);
      } else {
        setActiveWorkspace(undefined);
      }
    }
  }, [
    userSession,
    query,
    setUserSession,
    persistUserSession,
    activeWorkspace?.workspaceId,
    setActiveWorkspace,
  ]);

  // Loading the profile/workspaces failed (persistent 401, revoked token,
  // unexpected response) once the query has settled and there is no data.
  const loadFailed =
    Boolean(userSession) && !query.isLoading && (query.isError || !query.data);

  // On failure, sign the user out and clear the session *before* redirecting.
  // Leaving the Firebase user signed in would let LoginPage immediately bounce
  // them back to /load_workspace, producing a redirect loop on a persistent
  // error. Once the session is cleared the `!userSession` guard below sends the
  // user to /login with no Firebase user left to redirect them back.
  useEffect(() => {
    if (!loadFailed) return;
    void signOut(firebaseAuth).finally(() => {
      setUserSession(undefined);
      setActiveWorkspace(undefined);
      revokeUserSession();
    });
  }, [loadFailed, setUserSession, setActiveWorkspace, revokeUserSession]);

  const loadingScreen = (
    <div className="bg-surface-muted">
      <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12 h-screen">
        <HashLoader
          color="#7214FF"
          size={100}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
        <TextLoader />
      </div>
    </div>
  );

  // User not logged in are redirected to the login page
  if (!userSession) return <Navigate to="/login" />;

  if (query.isLoading) return loadingScreen;

  // Keep the loader on screen while the sign-out effect clears the session; the
  // `!userSession` guard above then redirects to /login (no loop).
  if (loadFailed) return loadingScreen;

  // Redirect based on whether user came from signup or login
  if (isFromSignup) {
    return <Navigate to="/onboarding" />;
  } else if (isSafeContinuePath(continuePath)) {
    return <Navigate to={continuePath} />;
  } else {
    return <Navigate to="/" />;
  }
};
